import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const SignupPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, name);
      toast.success('Account created! Let\'s set up your studio.');
      navigate('/app/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="signup-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="text-3xl font-bold text-gold-gradient">WrapViz</span>
        </Link>

        {/* Form Card */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-8">
          <h1 className="text-2xl font-bold mb-2">Create your account</h1>
          <p className="text-white/50 mb-8">Start your free trial today</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-white/70 mb-2 block">Your Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="bg-[#171717] border-white/10 focus:border-[#FFFFFF] h-12"
                data-testid="signup-name"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-white/70 mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
                className="bg-[#171717] border-white/10 focus:border-[#FFFFFF] h-12"
                data-testid="signup-email"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white/70 mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-[#171717] border-white/10 focus:border-[#FFFFFF] h-12 pr-12"
                  data-testid="signup-password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-white/30 mt-2">Minimum 6 characters</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFFFFF] text-black hover:bg-[#E0E0E0] font-bold uppercase tracking-wider h-12 btn-glow"
              data-testid="signup-submit"
            >
              {loading ? <span className="spinner w-5 h-5"></span> : 'Create Account'}
            </Button>
          </form>

          <p className="mt-6 text-xs text-white/30 text-center">
            By signing up, you agree to our{' '}
            <Link to="/terms" className="text-[#FFFFFF] hover:underline">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#FFFFFF] hover:underline">Privacy Policy</Link>
          </p>

          <p className="mt-8 text-center text-white/50">
            Already have an account?{' '}
            <Link to="/login" className="text-[#FFFFFF] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SignupPage;
